/**
 * Perf-only R3F/WebGL frame-commit ring buffer.
 * Active solely when `?perf=1` — zero overhead otherwise (callers must gate).
 * Samples are recorded from a post-EffectComposer useFrame (priority 2).
 */
export const PERF_COMMIT_METHOD = "r3f-post-effect-frame-commit" as const;

export interface PerfCommitSample {
  /** Monotonic sequence within the current sampling epoch. */
  seq: number;
  /** performance.now() after EffectComposer has rendered this frame. */
  t: number;
  sourceTick: number;
  editTick: number;
  /** Live PreviewFrameStore generation at commit time. */
  generation: number;
  /** Live PreviewFrameStore contentEpoch at commit time. */
  contentEpoch: number;
}

export interface PerfCommitSnapshot {
  method: typeof PERF_COMMIT_METHOD;
  seqStart: number;
  seqEnd: number;
  samples: PerfCommitSample[];
  intervalsMs: number[];
  skippedOrCoalescedUiTicks: number;
}

const RING_CAP = 512;

let enabled = false;
let seq = 0;
let ring: PerfCommitSample[] = [];
let coalescedUiTicks = 0;

export function isPerfCommitSamplingEnabled(
  search: string = typeof window !== "undefined" ? window.location.search : "",
): boolean {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return params.get("perf") === "1";
}

/** True only while the ring is actively accepting commits (after begin under ?perf=1). */
export function isPerfCommitSamplingActive(): boolean {
  return enabled;
}

/** Activate / clear the ring. No-ops when perf query is off. */
export function beginPerfCommitSampling(search?: string): boolean {
  enabled = isPerfCommitSamplingEnabled(search);
  seq = 0;
  ring = [];
  coalescedUiTicks = 0;
  return enabled;
}

export function endPerfCommitSampling(): void {
  enabled = false;
}

export function notePerfCoalescedUiTick(): void {
  if (!enabled) return;
  coalescedUiTicks += 1;
}

/**
 * Record one committed R3F frame after EffectComposer.
 * Ignored unless sampling began under ?perf=1.
 */
export function recordPerfFrameCommit(
  sourceTick: number,
  editTick: number,
  now = performance.now(),
  generation = 0,
  contentEpoch = 0,
): void {
  if (!enabled) return;
  seq += 1;
  const sample: PerfCommitSample = {
    seq,
    t: now,
    sourceTick: Math.floor(sourceTick),
    editTick: Math.floor(editTick),
    generation: Math.floor(generation),
    contentEpoch: Math.floor(contentEpoch),
  };
  ring.push(sample);
  if (ring.length > RING_CAP) {
    ring.splice(0, ring.length - RING_CAP);
  }
}

export function getPerfCommitSnapshot(): PerfCommitSnapshot | null {
  if (ring.length === 0) {
    return {
      method: PERF_COMMIT_METHOD,
      seqStart: 0,
      seqEnd: 0,
      samples: [],
      intervalsMs: [],
      skippedOrCoalescedUiTicks: coalescedUiTicks,
    };
  }
  const samples = [...ring];
  const intervalsMs: number[] = [];
  for (let i = 1; i < samples.length; i++) {
    const dt = samples[i]!.t - samples[i - 1]!.t;
    if (dt > 0 && dt < 5000) intervalsMs.push(dt);
  }
  return {
    method: PERF_COMMIT_METHOD,
    seqStart: samples[0]!.seq,
    seqEnd: samples[samples.length - 1]!.seq,
    samples,
    intervalsMs,
    skippedOrCoalescedUiTicks: coalescedUiTicks,
  };
}

/** Test helper — force-enable without URL (happy-dom). */
export function forceEnablePerfCommitSamplingForTests(): void {
  enabled = true;
  seq = 0;
  ring = [];
  coalescedUiTicks = 0;
}

export function resetPerfCommitSamplingForTests(): void {
  enabled = false;
  seq = 0;
  ring = [];
  coalescedUiTicks = 0;
}
